import { useEffect, useRef, useState } from 'react';
import useSWR, { Key } from 'swr';
import useSWRImmutable from 'swr/immutable';

import { PICKER_INTERACTION_KEY } from '@/constants/availability-telemetry.ts';

import {
    isAvailabilityInteractionOpen,
    settleAvailabilityInteraction,
    startAvailabilityInteraction,
} from '@/lib/availability-telemetry.ts';

import { AvailabilityResponse, WeekAvailability } from '@/models/Availability.ts';
import { DayjsUnit } from '@/models/Dayjs.ts';
import { Insurance } from '@/models/appointment/Appointment.ts';
import { EnrichedAppointmentType, Practitioner } from '@/models/appointment/EnrichedPractitionerData.ts';
import { AvailabilityAction, AvailabilityMode, MetricSource } from '@/models/metrics.ts';

import { getAvailableTimeslots } from '@/services/availabilityService.ts';

import { useConfigStore } from '@/stores/configStore.ts';

import { dayjs } from '@/utils/dayjsSetup.ts';
import { insuranceToInsuranceType } from '@/utils/insurance-utils.ts';

export const DAYS_PER_PAGE = 3;

interface TimeslotsParams {
    practitioner: Practitioner;
    insurance: Insurance;
    /** Which booking flow this picker belongs to. Comes from the caller, never from store state. */
    mode: AvailabilityMode;
    /** The interaction this picker's own paging opens and settles. */
    interactionKey: string;
    /** Whether this is the picker the operator is looking at, and so may settle the switch. */
    isExpandedRow?: boolean;
    appointmentType?: EnrichedAppointmentType;
    isReturning?: boolean;
    defaultStartDate?: string;
    selectedCalendarId?: string;
}

interface TimeslotsHook {
    availability: WeekAvailability[] | undefined;
    is_loading: boolean;
    error: Error | undefined;
    next_available_slot: string | undefined;
    has_slots: boolean | undefined;
    start_date: string;
    pageTo: (date: string, action: AvailabilityAction) => void;
}

// This picker's own paging wins. Otherwise only the expanded row may settle the switch. Otherwise
// nobody reports, which is what keeps background revalidation out of the metric.
const resolveSettleKey = (interactionKey: string, isExpandedRow?: boolean): string | undefined => {
    if (isAvailabilityInteractionOpen(interactionKey)) {
        return interactionKey;
    }

    if (isExpandedRow === true) {
        return PICKER_INTERACTION_KEY;
    }

    return undefined;
};

const settleTimeslotsResult = (key: string | undefined, error: Error | undefined, source: MetricSource): void => {
    if (key === undefined) {
        return;
    }

    if (error !== undefined) {
        settleAvailabilityInteraction({ key: key, outcome: 'failure', reason: 'availability_request_failed' });
        return;
    }

    settleAvailabilityInteraction({ key: key, outcome: 'success', source: source });
};

const useTimeslots = ({
    practitioner,
    insurance,
    mode,
    interactionKey,
    isExpandedRow,
    appointmentType,
    isReturning,
    defaultStartDate,
    selectedCalendarId,
}: TimeslotsParams): TimeslotsHook => {
    const [startDate, setStartDate] = useState<string>(dayjs(defaultStartDate).format('YYYY-MM-DD'));
    const { selected_location: selectedLocation, selected_customer: selectedCustomer } = useConfigStore(
        (state) => state
    );
    const hasSeenLoading = useRef(false);

    // Find the calendar that matches the selected calendar ID
    const selectedCalendar = practitioner.calendars.find((calendar) => calendar.pvs.id === selectedCalendarId);

    // Get the current appointment type ID (default to an empty string if undefined)
    const appointmentTypeId = appointmentType?.id ?? '';

    // Check if the selected calendar supports the given appointment type
    const selectedCalendarSupportsType = selectedCalendar?.appointment_type_ids.includes(appointmentTypeId);

    // If not, find another calendar that supports the appointment type
    const fallbackCalendar = practitioner.calendars.find((calendar) =>
        calendar.appointment_type_ids.includes(appointmentTypeId)
    );

    // Determine the final calendar ID
    const calendarId = selectedCalendarSupportsType ? selectedCalendarId : fallbackCalendar?.pvs.id;

    const timeslotsKey = (date: string): Key =>
        appointmentType
            ? [
                  'available-timeslots',
                  insurance,
                  date,
                  appointmentType.id,
                  practitioner.id,
                  isReturning ? 'returning' : 'new',
              ]
            : undefined;

    const fetchTimeslots = (date: string): Promise<AvailabilityResponse> =>
        getAvailableTimeslots({
            pvs_provider: practitioner.pvs_provider,
            customer_id: selectedCustomer?.customer_id ?? '',
            user_id: practitioner.id,
            location_key: selectedLocation?.key ?? '',
            appointment_type_key: appointmentType?.prismic_key ?? '',
            insurance_type: insuranceToInsuranceType(insurance),
            pvs_calendar_id: calendarId ?? '',
            start_date: date,
            is_returning: isReturning,
            is_online: appointmentType?.is_online ?? false,
        });

    const { data, error, isLoading } = useSWR<AvailabilityResponse, Error>(
        timeslotsKey(startDate),
        () => fetchTimeslots(startDate),
        { refreshInterval: 20000 }
    );

    const nextPageStartDate = dayjs(startDate).add(DAYS_PER_PAGE, DayjsUnit.DAY).format('YYYY-MM-DD');

    useSWRImmutable<AvailabilityResponse, Error>(
        isExpandedRow === true && data !== undefined ? timeslotsKey(nextPageStartDate) : undefined,
        () => fetchTimeslots(nextPageStartDate)
    );

    // The settle is observed here rather than in a click handler because SWR answers an already
    // fetched range without ever rendering a loading state, and that paint is what was waited for.
    useEffect(() => {
        hasSeenLoading.current = hasSeenLoading.current || isLoading;

        const hasSettled = !isLoading && (data !== undefined || error !== undefined);
        const settleKey = hasSettled ? resolveSettleKey(interactionKey, isExpandedRow) : undefined;

        // No loading render since the interaction started means the data was already in the cache.
        settleTimeslotsResult(settleKey, error, hasSeenLoading.current ? 'network' : 'cache');

        // The window is one interaction: it closes here and reopens in pageTo.
        hasSeenLoading.current = settleKey === undefined && hasSeenLoading.current;
    }, [data, error, isLoading, interactionKey, isExpandedRow]);

    const hasSlots = data?.availability?.slice(0, DAYS_PER_PAGE).some(({ slots }) => slots.length > 0);
    const firstDateWithSlots = data?.availability?.find(({ slots }) => slots.length > 0)?.date;

    const pageTo = (date: string, action: AvailabilityAction): void => {
        hasSeenLoading.current = false;
        startAvailabilityInteraction({ key: interactionKey, mode: mode, action: action });
        setStartDate(date);

        // Without an appointment type there is no SWR key, so no request and no settle would follow.
        if (appointmentType === undefined) {
            settleAvailabilityInteraction({
                key: interactionKey,
                outcome: 'failure',
                reason: 'availability_config_incomplete',
            });
        }
    };

    return {
        availability: data?.availability?.slice(0, DAYS_PER_PAGE),
        is_loading: isLoading,
        error: error,
        next_available_slot: data?.next_slot || firstDateWithSlots,
        has_slots: hasSlots,
        start_date: startDate,
        pageTo: pageTo,
    };
};

export default useTimeslots;
